import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import DeleteRecordButton, { deleteNeedsReason } from '../../components/DeleteRecordButton';
import { AuditTrailViewer } from '../../components/AuditTrail';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import { ASSESSMENT_TYPE_DESCRIPTIONS } from '../../utils/hiraConstants';
import RiskMatrix5x5 from '../../components/RiskMatrix5x5';
import { usePermissions } from '../../context/PermissionContext';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { formatRoleName } from '../../utils/roleUtils';
import { departmentLabel } from '../../utils/departmentLabel';
import {
  DEFAULT_RISK_BANDS,
  fetchRiskBandConfig,
  resolveBandLabel,
  getBandColorForLabel,
  isAcceptable,
} from '../../utils/riskBands';
import { DEFAULT_SCALE_LABELS, fetchScaleLabelConfig } from '../../utils/scaleLabels';

// Incident numbers are stored with their own prefix (e.g. "INC-2026-010") —
// only prepend "INC-" when it's missing.
const formatIncidentNumber = (n) =>
  !n ? '' : String(n).toUpperCase().startsWith('INC') ? String(n) : `INC-${n}`;

// Normalize existing-control entries: legacy records are plain strings,
// new records are {description, control_type} objects.
const normalizeExistingControls = (list) =>
  (list || [])
    .map((c) =>
      typeof c === 'string'
        ? { description: c, control_type: 'Administrative' }
        : c && c.description
        ? { description: c.description, control_type: c.control_type || 'Administrative' }
        : null
    )
    .filter(Boolean);

// QA #85 display pair for a hazard card.
//
// `hazards.title` predates the Hazard Name field: rows created before it was
// wired had title backfilled from the description, truncated to 255 chars
// (routes/hira.py _create_hazard_row). Treating such a title as a "name" would
// render a truncated heading with the full description repeated underneath.
// So a title that is merely a prefix of the description is not a real name.
const hazardDisplay = (hazard) => {
  const title = (hazard.title || '').trim();
  const description = (hazard.hazard_description || '').trim();
  const isBackfilledFromDescription =
    !title || title === description || description.startsWith(title);
  return isBackfilledFromDescription
    ? { name: description || title, sub: null }
    : { name: title, sub: description || null };
};

const HIRADetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showHazardModal, setShowHazardModal] = useState(false);
  const [editingHazard, setEditingHazard] = useState(null);

  // Form state for assessment editing
  const [formData, setFormData] = useState({});
  
  // Hazard form state
  const [hazardForm, setHazardForm] = useState({
    // QA #85: hazard NAME, persisted to hazards.title. Previously the typed
    // custom-hazard name lived only in `hazardSearchText` (local state) and was
    // copied into hazard_description, so it was lost on reload.
    title: '',
    hazard_description: '',
    who_at_risk: '',
    consequence: '',
    process: '',
    activity: '',
    activity_type: 'Routine',
    legal_requirement: false,
    emergency_condition: false,
    severity: 3,
    likelihood: 3,
    existing_controls: [],
    control_measures: []
  });

  // Tenant risk bands (display only — server recomputes authoritatively)
  const [riskBands, setRiskBands] = useState(DEFAULT_RISK_BANDS);
  // Tenant severity/probability wording (1-5 integers stay canonical)
  const [scaleLabels, setScaleLabels] = useState(DEFAULT_SCALE_LABELS);
  // Per-hazard CAPA links: {hazard_id: [{capa_id, title, status}]}
  const [hazardCapaLinks, setHazardCapaLinks] = useState({});
  // Per-hazard incident links: {hazard_id: [{link_id, incident_id, incident_number, title, severity, status}]}
  const [hazardIncidentLinks, setHazardIncidentLinks] = useState({});
  // "Link Incident" picker state
  const [linkIncidentHazard, setLinkIncidentHazard] = useState(null); // hazard object or null (modal closed)
  const [incidentSearch, setIncidentSearch] = useState('');
  const [incidentResults, setIncidentResults] = useState([]);
  const [incidentSearchLoading, setIncidentSearchLoading] = useState(false);
  const [incidentSearchError, setIncidentSearchError] = useState('');

  const [newControl, setNewControl] = useState({
    control_type: 'Engineering',
    description: '',
    responsibility: '',
    timeline: '',
    status: 'Pending'
  });

  const [existingControlInput, setExistingControlInput] = useState('');
  const [existingControlType, setExistingControlType] = useState('Administrative');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  
  // New state for enhanced features
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showCAPAModal, setShowCAPAModal] = useState(false);
  const [showJSAModal, setShowJSAModal] = useState(false);
  const [editingJSAStep, setEditingJSAStep] = useState(null);
  const [hazardLibrary, setHazardLibrary] = useState([]);
  const [selectedLibraryHazard, setSelectedLibraryHazard] = useState('');

  // Searchable hazard dropdown state
  const [hazardSearchText, setHazardSearchText] = useState('');
  const [showHazardDropdown, setShowHazardDropdown] = useState(false);
  const [isCustomHazard, setIsCustomHazard] = useState(false);
  // "Save to library" opt-in (only meaningful when isCustomHazard === true).
  // When checked on Save Hazard, we POST /hazard-library first, then proceed
  // with the normal risk-assessment hazard update.
  const [saveToLibrary, setSaveToLibrary] = useState(false);
  const [libraryCategory, setLibraryCategory] = useState('Physical');
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  // QA #87d: responsible_person_id is the real user id. This modal used to send
  // only a free-text name, so a HIRA-sourced CAPA had no assignee id at all -
  // invisible to "assigned to me" queries and to the reminder/overdue jobs - and
  // notified nobody. Field renamed to "Assigned To" and made mandatory to match
  // the standalone CAPA form, per QA.
  const [capaForm, setCAPAForm] = useState({
    hazard_id: '',
    corrective_action: '',
    preventive_action: '',
    responsible_person: '',
    responsible_person_id: '',
    due_date: ''
  });
  
  // Assignee options for the CAPA modal (QA #87d). Uses the existing
  // least-privilege directory endpoint (GET /api/users/directory), which returns
  // id/name/role only and needs no users:read grant. Scoped to the assessment's
  // site so the picker never widens past the caller's site scope.
  const [assignableUsers, setAssignableUsers] = useState([]);

  // JSA Step form state
  const [jsaStepForm, setJsaStepForm] = useState({
    step_sequence: 1,
    step_description: '',
    hazard_description: '',
    existing_controls: [],
    recommended_controls: []
  });
  
  const [jsaControlInput, setJsaControlInput] = useState('');
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnNotes, setReturnNotes] = useState('');
  const [previousAssessments, setPreviousAssessments] = useState([]);
  const { activeSiteId } = useSite();
  const { hasPermission } = usePermissions();

  // Cross-site read-only: if the record has a read_only flag from the API, disable editing
  const isCrossSiteReadOnly = assessment?.read_only === true;

  useEffect(() => {
    fetchAssessment();
    fetchHazardLibrary();
    fetchUsers();
    fetchAssignableUsers();
    fetchPreviousAssessments();
    fetchRiskBandConfig().then(cfg => setRiskBands(cfg.bands || DEFAULT_RISK_BANDS));
    fetchScaleLabelConfig().then(cfg => setScaleLabels({
      severity: cfg.severity || DEFAULT_SCALE_LABELS.severity,
      probability: cfg.probability || DEFAULT_SCALE_LABELS.probability,
    }));
    const token = localStorage.getItem('token');
    axios.get(`${API}/departments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDepartments(res.data.departments || []))
      .catch(() => setDepartments([]));
  }, [id, activeSiteId]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = activeSiteId ? `${API}/users?site_id=${activeSiteId}` : `${API}/users`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // QA #87d: assignee options for the CAPA modal.
  //
  // Deliberately NOT reusing `users` above: fetchUsers() calls GET /api/users,
  // which requires the users:read permission. That is the same trap as QA #36 -
  // a picker that silently empties for any role without user-management rights.
  // GET /api/users/directory returns id/name/role only, needs no users:read, and
  // is site-scoped, so a Safety Officer raising a CAPA can pick an owner without
  // being able to see user-management data (least privilege, CC6.1).
  const fetchAssignableUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = activeSiteId
        ? `${API}/users/directory?site_id=${encodeURIComponent(activeSiteId)}`
        : `${API}/users/directory`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignableUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Leave the list empty; the modal shows an explanatory note rather than
      // an empty dropdown with no reason given.
      console.error('Error fetching assignable users:', error);
      setAssignableUsers([]);
    }
  };

  const fetchPreviousAssessments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/risk-assessments?status=Active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out current assessment - list endpoint returns a plain array
      const allAssessments = Array.isArray(response.data) ? response.data : (response.data.risk_assessments || []);
      const assessments = allAssessments.filter(ra => ra.id !== id);
      setPreviousAssessments(assessments);
    } catch (error) {
      console.error('Error fetching previous assessments:', error);
    }
  };

  const fetchHazardLibrary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hazard-library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHazardLibrary(response.data);
    } catch (err) {
      console.error('Failed to fetch hazard library:', err);
    }
  };

  const fetchAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/risk-assessments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessment(response.data);
      setFormData({
        title: response.data.title,
        description: response.data.description || '',
        assessment_type: response.data.assessment_type,
        department: response.data.department || '',
        department_id: response.data.department_id || '',
        location: response.data.location || '',
        status: response.data.status,
        review_date: response.data.review_date ? response.data.review_date.split('T')[0] : '',
        // MOM Fields
        meeting_datetime: response.data.meeting_datetime ? response.data.meeting_datetime.slice(0, 16) : '',
        meeting_location: response.data.meeting_location || '',
        facilitator_id: response.data.facilitator_id || '',
        facilitator_name: response.data.facilitator_name || '',
        mom_attendees: response.data.mom_attendees || [],
        previous_assessment_id: response.data.previous_assessment_id || '',
        previous_actions_review: response.data.previous_actions_review || []
      });
    } catch (err) {
      console.error('Failed to fetch assessment:', err);
    } finally {
      setLoading(false);
    }
    fetchHazardCapaLinks();
    fetchHazardIncidentLinks();
  };

  const fetchHazardIncidentLinks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/risk-assessments/${id}/hazard-incident-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHazardIncidentLinks(response.data || {});
    } catch (err) {
      // Non-fatal: cards just render without incident chips
      console.error('Failed to fetch hazard incident links:', err);
    }
  };

  const fetchHazardCapaLinks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/risk-assessments/${id}/hazard-capa-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHazardCapaLinks(response.data || {});
    } catch (err) {
      // Non-fatal: cards just render without link chips
      console.error('Failed to fetch hazard CAPA links:', err);
    }
  };

  const handleUpdateAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/risk-assessments/${id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditing(false);
      fetchAssessment();
    } catch (err) {
      console.error('Failed to update assessment:', err);
      showError('Failed to update assessment');
    }
  };

  const calculateRiskScore = (severity, likelihood) => severity * likelihood;

  // Display-only band resolution against the tenant's configurable risk
  // bands. The server recomputes both values authoritatively on save.
  const calculateRiskLevel = (score) => resolveBandLabel(score, riskBands);

  // Inline style for a risk badge — band colors are tenant-configurable so
  // Tailwind class maps can't cover custom labels (legacy labels fall back
  // to their historical colors).
  const getRiskBadgeStyle = (level) => ({
    backgroundColor: getBandColorForLabel(level, riskBands),
    color: '#FFFFFF',
  });

  // True when a residual/initial level would block approval (band verdict)
  const isBlockingLevel = (score) => !isAcceptable(score, riskBands);

  const openHazardModal = (hazard = null) => {
    if (hazard) {
      setEditingHazard(hazard);
      setHazardForm({
        title: hazard.title || '',
        hazard_description: hazard.hazard_description,
        who_at_risk: hazard.who_at_risk || '',
        consequence: hazard.consequence || '',
        process: hazard.process || '',
        activity: hazard.activity || '',
        activity_type: hazard.activity_type || 'Routine',
        legal_requirement: !!hazard.legal_requirement,
        emergency_condition: !!hazard.emergency_condition,
        severity: hazard.severity,
        likelihood: hazard.likelihood,
        existing_controls: normalizeExistingControls(hazard.existing_controls),
        control_measures: hazard.control_measures || [],
        residual_severity: hazard.residual_severity || null,
        residual_likelihood: hazard.residual_likelihood || null
      });
    } else {
      setEditingHazard(null);
      setHazardForm({
        title: '',
        hazard_description: '',
        who_at_risk: '',
        consequence: '',
        process: '',
        activity: '',
        activity_type: 'Routine',
        legal_requirement: false,
        emergency_condition: false,
        severity: 3,
        likelihood: 3,
        existing_controls: [],
        control_measures: [],
        residual_severity: null,
        residual_likelihood: null
      });
    }
    // Reset AI suggestions and library selection
    setAiSuggestions([]);
    setSelectedLibraryHazard('');
    setHazardSearchText('');
    setShowHazardDropdown(false);
    setIsCustomHazard(false);
    setShowHazardModal(true);
  };

  const handleSaveHazard = async () => {
    const riskScore = calculateRiskScore(hazardForm.severity, hazardForm.likelihood);
    const riskLevel = calculateRiskLevel(riskScore);

    const hazardData = {
      ...hazardForm,
      risk_score: riskScore,
      risk_level: riskLevel
    };

    // Calculate residual risk if provided
    if (hazardForm.residual_severity && hazardForm.residual_likelihood) {
      hazardData.residual_risk_score = calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood);
      hazardData.residual_risk_level = calculateRiskLevel(hazardData.residual_risk_score);
    }

    let updatedHazards;
    if (editingHazard) {
      // Update existing hazard
      updatedHazards = assessment.hazards.map(h =>
        h.id === editingHazard.id ? { ...editingHazard, ...hazardData } : h
      );
    } else {
      // Add new hazard
      updatedHazards = [...(assessment.hazards || []), hazardData];
    }

    try {
      const token = localStorage.getItem('token');

      // If the user entered a custom hazard AND opted in, persist it to
      // the shared hazard library first so future risk assessments can
      // pick it from the dropdown. Failure here is non-fatal — we log
      // and still save the hazard to the current RA.
      if (isCustomHazard && saveToLibrary && hazardSearchText.trim()) {
        setSavingToLibrary(true);
        try {
          await axios.post(
            `${API}/hazard-library`,
            {
              category: libraryCategory,
              hazard_name: hazardSearchText.trim(),
              hazard_description: hazardForm.hazard_description || '',
              default_consequences: '',
              // Seed the library entry with any control measures the user
              // has already added on this RA hazard — future users picking
              // this library item will see them as suggested_controls.
              suggested_controls: [
                ...normalizeExistingControls(hazardForm.existing_controls).map(c => c.description),
                ...(hazardForm.control_measures || []).map(c => c.description).filter(Boolean),
              ],
              severity_guidance: hazardForm.severity,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Refresh the library list so the new entry appears in the
          // dropdown without a page reload.
          await fetchHazardLibrary();
        } catch (libErr) {
          console.error('Failed to save hazard to library:', libErr);
          showError(
            libErr.response?.data?.detail ||
            'Could not save to library; hazard will still be added to this assessment.'
          );
        } finally {
          setSavingToLibrary(false);
        }
      }

      await axios.put(
        `${API}/risk-assessments/${id}`,
        { hazards: updatedHazards },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowHazardModal(false);
      // Reset the library-save opt-in so the next hazard starts clean.
      setSaveToLibrary(false);
      fetchAssessment();
    } catch (err) {
      console.error('Failed to save hazard:', err);
      showError('Failed to save hazard');
    }
  };

  const handleDeleteHazard = async (hazardId) => {
    const confirmed = await confirm('Are you sure you want to delete this hazard?');
    if (!confirmed) return;

    const updatedHazards = assessment.hazards.filter(h => h.id !== hazardId);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/risk-assessments/${id}`,
        { hazards: updatedHazards },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAssessment();
    } catch (err) {
      console.error('Failed to delete hazard:', err);
      showError('Failed to delete hazard');
    }
  };

  const addExistingControl = () => {
    if (existingControlInput.trim()) {
      setHazardForm(prev => ({
        ...prev,
        existing_controls: [
          ...prev.existing_controls,
          { description: existingControlInput.trim(), control_type: existingControlType }
        ]
      }));
      setExistingControlInput('');
    }
  };

  const removeExistingControl = (index) => {
    setHazardForm(prev => ({
      ...prev,
      existing_controls: prev.existing_controls.filter((_, i) => i !== index)
    }));
  };

  const addControlMeasure = () => {
    if (newControl.description.trim()) {
      setHazardForm(prev => ({
        ...prev,
        control_measures: [...prev.control_measures, { ...newControl }]
      }));
      setNewControl({
        control_type: 'Engineering',
        description: '',
        responsibility: '',
        timeline: '',
        status: 'Pending'
      });
    }
  };

  const removeControlMeasure = (index) => {
    setHazardForm(prev => ({
      ...prev,
      control_measures: prev.control_measures.filter((_, i) => i !== index)
    }));
  };

  const updateControlMeasure = (index, field, value) => {
    setHazardForm(prev => ({
      ...prev,
      control_measures: prev.control_measures.map((ctrl, i) =>
        i === index ? { ...ctrl, [field]: value } : ctrl
      )
    }));
  };

  const fetchAISuggestions = async () => {
    if (!hazardForm.hazard_description.trim()) {
      showError('Please enter a hazard description first');
      return;
    }

    setLoadingAI(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/risk-assessments/${id}/suggest-controls`,
        {
          hazard_description: hazardForm.hazard_description,
          severity: hazardForm.severity,
          likelihood: hazardForm.likelihood,
          who_at_risk: hazardForm.who_at_risk,
          existing_controls: hazardForm.existing_controls || [],
          control_measures: hazardForm.control_measures || []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAiSuggestions(response.data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch AI suggestions:', err);
      showError('Failed to get AI suggestions. Please try again.');
    } finally {
      setLoadingAI(false);
    }
  };

  const addAISuggestion = (suggestion) => {
    // Check for duplicates against existing controls and control measures
    const allExisting = [
      ...normalizeExistingControls(hazardForm.existing_controls).map(c => c.description),
      ...(hazardForm.control_measures || []).map(m => m.description)
    ].map(d => (d || '').toLowerCase().trim());

    const suggLower = suggestion.description.toLowerCase().trim();
    const isDuplicate = allExisting.some(
      existing => existing.includes(suggLower) || suggLower.includes(existing)
    );

    if (isDuplicate) {
      showError('This control measure already exists or is very similar to an existing one.');
      return;
    }

    setHazardForm(prev => ({
      ...prev,
      control_measures: [
        ...prev.control_measures,
        {
          control_type: suggestion.control_type,
          description: suggestion.description,
          responsibility: '',
          timeline: '',
          status: 'Pending'
        }
      ]
    }));
    // Remove the suggestion from the list
    setAiSuggestions(prev => prev.filter(s => s !== suggestion));
  };

  // Approval workflow handlers
  const handleApprovalAction = async (action, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/risk-assessments/${id}/approval-action`,
        { action, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(response.data.message);
      fetchAssessment();
    } catch (err) {
      // The approval gate returns a structured detail
      // {code: 'UNACCEPTABLE_RISK', message, hazards: [{description, reason}]}
      // — render it readably instead of "[object Object]".
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'UNACCEPTABLE_RISK') {
        const lines = (detail.hazards || [])
          .map(h => `• ${h.description}: ${h.reason}`)
          .join('\n');
        showError(`${detail.message}\n${lines}`);
      } else if (typeof detail === 'string') {
        showError(detail);
      } else {
        showError('Action failed');
      }
    }
  };

  const handleCreateVersion = async (changeType, notes) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/risk-assessments/${id}/create-version`,
        { change_type: changeType, change_notes: notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('New version created successfully');
      navigate(`/hira/${response.data.id}`);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to create version');
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/risk-assessments/${id}/export-excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HIRA_EHSMS-F-12_${id.substring(0, 8)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      showError('Failed to export HIRA Excel register');
    }
  };

  const handleExportMOM = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/risk-assessments/${id}/export-mom`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MOM_Risk_Assessment_${id.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      showError('Failed to export MOM report');
    }
  };

  const handleCreateCAPA = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/risk-assessments/${id}/create-capa`,
        capaForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('CAPA created and linked successfully');
      setShowCAPAModal(false);
      setCAPAForm({
        hazard_id: '',
        corrective_action: '',
        preventive_action: '',
        responsible_person: '',
        responsible_person_id: '',
        due_date: ''
      });
      fetchAssessment();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to create CAPA');
    }
  };

  // ---- Link Incident picker ----
  const openLinkIncidentModal = (hazard) => {
    setLinkIncidentHazard(hazard);
    setIncidentSearch('');
    setIncidentResults([]);
    setIncidentSearchError('');
  };

  // Debounced incident search (fires once on open with the empty query)
  useEffect(() => {
    if (!linkIncidentHazard) return;
    const timer = setTimeout(async () => {
      setIncidentSearchLoading(true);
      setIncidentSearchError('');
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ limit: '20' });
        if (incidentSearch.trim()) params.append('search', incidentSearch.trim());
        if (assessment?.site_id) params.append('site_id', assessment.site_id);
        const response = await axios.get(`${API}/incidents?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data;
        setIncidentResults(Array.isArray(data) ? data : (data.items || []));
      } catch (err) {
        if (err.response?.status === 403) {
          setIncidentSearchError("You don't have permission to browse incidents.");
        } else {
          setIncidentSearchError('Failed to load incidents. Please try again.');
        }
        setIncidentResults([]);
      } finally {
        setIncidentSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [linkIncidentHazard, incidentSearch]); // assessment.site_id is stable while the modal is open

  const handleLinkIncident = async (incidentId) => {
    if (!linkIncidentHazard) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/risk-assessments/${id}/hazards/${linkIncidentHazard.id}/incident-links`,
        { incident_id: incidentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Incident linked to hazard');
      fetchHazardIncidentLinks();
    } catch (err) {
      const detail = err.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Failed to link incident');
    }
  };

  const handleUnlinkIncident = async (link) => {
    const confirmed = await confirm(`Unlink incident ${formatIncidentNumber(link.incident_number)} from this hazard?`);
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hazard-incident-links/${link.link_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchHazardIncidentLinks();
    } catch (err) {
      const detail = err.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Failed to unlink incident');
    }
  };

  const openCAPAModal = (hazard, index) => {
    setCAPAForm({
      hazard_id: hazard.id || String(index),
      corrective_action: '',
      preventive_action: '',
      responsible_person: '',
      responsible_person_id: '',
      due_date: ''
    });
    setShowCAPAModal(true);
  };

  // Filter hazards based on search text (minimum 2 characters for filtering)
  const filteredHazards = hazardSearchText.length >= 2
    ? hazardLibrary.filter(h =>
        h.hazard_name.toLowerCase().includes(hazardSearchText.toLowerCase()) ||
        h.category.toLowerCase().includes(hazardSearchText.toLowerCase())
      )
    : hazardLibrary;

  const handleHazardSearchChange = (e) => {
    const value = e.target.value;
    setHazardSearchText(value);
    setShowHazardDropdown(true);
    setIsCustomHazard(false);
    setSelectedLibraryHazard('');
  };

  const handleHazardSelect = (hazard) => {
    setSelectedLibraryHazard(hazard.id);
    setHazardSearchText(`[${hazard.category}] ${hazard.hazard_name}`);
    setShowHazardDropdown(false);
    setIsCustomHazard(false);

    setHazardForm(prev => ({
      ...prev,
      title: hazard.hazard_name,
      hazard_description: hazard.hazard_description,
      severity: hazard.severity_guidance || prev.severity,
      existing_controls: [],
      control_measures: hazard.suggested_controls.map(ctrl => ({
        control_type: 'Engineering',
        description: ctrl,
        responsibility: '',
        timeline: '',
        status: 'Pending'
      }))
    }));
  };

  const handleUseCustomHazard = () => {
    setIsCustomHazard(true);
    setShowHazardDropdown(false);
    setSelectedLibraryHazard('');
    // QA #85: the typed text is the hazard NAME. Persist it as `title` (which
    // reaches hazards.title) instead of only overloading hazard_description.
    // The description is still seeded from it when blank, so nothing regresses
    // for users who type a name and no separate description.
    if (hazardSearchText) {
      setHazardForm(prev => ({
        ...prev,
        title: hazardSearchText,
        hazard_description: prev.hazard_description || hazardSearchText
      }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return 'bg-gray-100 text-gray-700';
      case 'Under Review': return 'bg-blue-100 text-blue-700';
      case 'Active': return 'bg-green-100 text-green-700';
      case 'Archived': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // JSA Step handlers
  const openJSAModal = (step = null) => {
    if (step) {
      setEditingJSAStep(step);
      setJsaStepForm({
        step_sequence: step.step_sequence,
        step_description: step.step_description,
        hazard_description: step.hazard_description,
        existing_controls: step.existing_controls || [],
        recommended_controls: step.recommended_controls || []
      });
    } else {
      const nextSeq = (assessment.jsa_steps?.length || 0) + 1;
      setEditingJSAStep(null);
      setJsaStepForm({
        step_sequence: nextSeq,
        step_description: '',
        hazard_description: '',
        existing_controls: [],
        recommended_controls: []
      });
    }
    setJsaControlInput('');
    setShowJSAModal(true);
  };

  const handleSaveJSAStep = async () => {
    if (!jsaStepForm.step_description || !jsaStepForm.hazard_description) {
      showError('Please fill in step description and hazard description');
      return;
    }

    const stepData = {
      id: editingJSAStep?.id || `jsa-${Date.now()}`,
      ...jsaStepForm
    };

    let updatedSteps;
    if (editingJSAStep) {
      updatedSteps = assessment.jsa_steps.map(s =>
        s.id === editingJSAStep.id ? stepData : s
      );
    } else {
      updatedSteps = [...(assessment.jsa_steps || []), stepData];
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/risk-assessments/${id}`,
        { jsa_steps: updatedSteps },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowJSAModal(false);
      fetchAssessment();
    } catch (err) {
      showError('Failed to save JSA step');
    }
  };

  const handleDeleteJSAStep = async (stepId) => {
    const confirmed = await confirm('Delete this JSA step?');
    if (!confirmed) return;

    const updatedSteps = assessment.jsa_steps.filter(s => s.id !== stepId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/risk-assessments/${id}`,
        { jsa_steps: updatedSteps },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAssessment();
    } catch (err) {
      showError('Failed to delete JSA step');
    }
  };

  const addJSAControl = () => {
    if (jsaControlInput.trim()) {
      setJsaStepForm(prev => ({
        ...prev,
        existing_controls: [...prev.existing_controls, jsaControlInput.trim()]
      }));
      setJsaControlInput('');
    }
  };

  const removeJSAControl = (index) => {
    setJsaStepForm(prev => ({
      ...prev,
      existing_controls: prev.existing_controls.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">Assessment not found</p>
        </div>
      </div>
      </div>
    );
  }

  const isActive = assessment.status === 'Active' || assessment.status === 'Archived';
  const canApprove = hasPermission('hira', 'approve');
  // CMP-06: the submitter may not approve their own assessment (enforced by
  // the API; the button is hidden here so the option is not offered).
  const isOwnSubmission = Boolean(
    assessment.submitted_by && user &&
    (assessment.submitted_by === user.id || assessment.submitted_by === user.user_id)
  );
  // CMP-14: the API returns validity_end_date (not validity_expiry_date) and
  // has no revalidation_required flag, so the banner and "Expires:" never
  // rendered. Derive both here with the same rule as
  // GET /risk-assessments/check-revalidation: an Active assessment that has
  // expired or expires within 30 days needs revalidation.
  const validityEndDate = assessment.validity_end_date || assessment.validity_expiry_date || null;
  const daysUntilExpiry = validityEndDate
    ? Math.floor((new Date(validityEndDate).getTime() - Date.now()) / 86400000)
    : null;
  const isExpiringSoon = Boolean(
    assessment.revalidation_required ||
    (assessment.status === 'Active' && daysUntilExpiry !== null && daysUntilExpiry <= 30)
  );

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />

        {/* Return-to-Draft notes modal (replaces the native browser prompt) */}
        {showReturnModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-bold text-gray-900">Return to Draft</h3>
              </div>
              <div className="px-6 py-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">Review notes <span className="text-red-500">*</span></label>
                <textarea
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                  rows={4}
                  autoFocus
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Reason for returning this assessment to draft…"
                />
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={() => setShowReturnModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowReturnModal(false);
                    handleApprovalAction('return_to_draft', returnNotes.trim());
                  }}
                  disabled={!returnNotes.trim()}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                >
                  Return to Draft
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/hira')}
            className="text-[#1B5C94] hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Risk Assessments
          </button>

          {isCrossSiteReadOnly && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-3 mb-4">
              <p className="text-sm text-amber-700">
                This record is from <strong>{assessment.site_name}</strong> and is read-only.
              </p>
            </div>
          )}

          {isExpiringSoon && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
              <p className="text-sm text-yellow-700">⚠️ <strong>Revalidation Required:</strong> This assessment expires soon. Please review and update.</p>
            </div>
          )}

          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                {editing ? (
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="text-3xl font-bold text-gray-800 border-b-2 border-[#1B5C94] focus:outline-none w-full"
                  />
                ) : (
                  <>
                    <h1 className="text-3xl font-bold text-gray-800 break-words">{assessment.title}</h1>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(assessment.status)}`}>
                      {assessment.status.replace(/_/g, ' ')}
                    </span>
                    <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-bold">
                      v{assessment.version_number}
                    </span>
                  </>
                )}
              </div>
              <div className="flex gap-4 mt-2 text-sm text-gray-600">
                {assessment.site_name && (
                  <span className="font-medium text-blue-700">Site: {assessment.site_name}</span>
                )}
                <span>Created: {formatDate(assessment.created_at)}</span>
                <span>Assessor: {assessment.assessor_name}</span>
                {validityEndDate && (
                  <span>Expires: {formatDate(validityEndDate)}</span>
                )}
              </div>
            </div>

            <div className="flex gap-3 flex-shrink-0">
              {hasPermission('hira', 'export') && (
                <button
                  onClick={handleExportExcel}
                  className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 flex items-center gap-2"
                  title="Export EHSMS-F-12 Excel register (ISO 45001)"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export Excel
                </button>
              )}
              {hasPermission('hira', 'export') && (
                <button
                  onClick={handleExportMOM}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  title="Export Minutes of Meeting PDF"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export MOM
                </button>
              )}

              {!isActive && !isCrossSiteReadOnly && hasPermission('capa', 'create') && (
                <button
                  onClick={() => {
                    if (!assessment.hazards || assessment.hazards.length === 0) {
                      showError('Please add at least one hazard before creating a CAPA');
                      return;
                    }
                    setShowCAPAModal(true);
                  }}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                    assessment.hazards && assessment.hazards.length > 0
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-orange-300 text-white cursor-not-allowed'
                  }`}
                  title={!assessment.hazards || assessment.hazards.length === 0 ? 'Add hazards first to create CAPA' : 'Create CAPA from hazard'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Create CAPA
                </button>
              )}

              {assessment.status === 'Active' && canApprove && (
                <button
                  onClick={() => setShowVersionModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  New Version
                </button>
              )}

              {editing ? (
                <>
                  <button
                    onClick={() => setEditing(false)}
                    className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateAssessment}
                    className="px-4 py-2 bg-[#1B5C94] text-white rounded-lg hover:bg-[#164A7B]"
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <>
                  {hasPermission('hira', 'update') && !isCrossSiteReadOnly && (
                    <button
                      onClick={() => !isActive && setEditing(true)}
                      disabled={isActive}
                      className={`px-4 py-2 rounded-lg ${isActive ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#1B5C94] text-white hover:bg-[#164A7B]'}`}
                      title={isActive ? `Cannot edit a risk assessment in '${assessment.status}' status` : 'Edit this assessment'}
                    >
                      Edit
                    </button>
                  )}
                  {/* Soft delete — any status (reason required unless Draft);
                      backend enforces hira:delete + site access + the reason rule. */}
                  {hasPermission('hira', 'delete') && !isCrossSiteReadOnly && (
                    <DeleteRecordButton
                      url={`${API}/risk-assessments/${id}`}
                      label={`risk assessment ${assessment.assessment_number || assessment.title || ''}`.trim()}
                      requireReason={deleteNeedsReason(assessment.status)}
                      onDeleted={() => navigate('/hira')}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-[#1B5C94] text-[#1B5C94]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-[#1B5C94] text-[#1B5C94]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
        {/* Approval Workflow Buttons */}
        {!isActive && (
          <div className="bg-white rounded-xl shadow-md p-4 mb-6">
            <h3 className="font-bold text-gray-800 mb-3">Workflow Actions</h3>
            <div className="flex gap-3">
              {assessment.status === 'Draft' && hasPermission('hira', 'update') && (
                <button
                  onClick={() => handleApprovalAction('submit')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  📤 Submit for Review
                </button>
              )}

              {canApprove && assessment.status === 'Under Review' && (
                <>
                  {isOwnSubmission ? (
                    <span className="px-3 py-2 text-sm text-gray-600 italic">
                      Submitted by you: another approver must approve
                    </span>
                  ) : (
                  <button
                    onClick={() => handleApprovalAction('approve')}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    ✅ Approve
                  </button>
                  )}
                  <button
                    onClick={() => { setReturnNotes(''); setShowReturnModal(true); }}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                  >
                    🔄 Return to Draft
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Assessment Details Card */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Assessment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title - only shown in edit mode within the card */}
            {editing && (
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Assessment Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Welding Operations - Main Workshop"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Type</label>
              {editing ? (
                <>
                <select
                  value={formData.assessment_type}
                  onChange={(e) => setFormData({ ...formData, assessment_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Activity-based">Activity-based</option>
                  <option value="Task-based">Task-based</option>
                  <option value="JSA">Job Safety Analysis (JSA)</option>
                  <option value="General">General Assessment</option>
                </select>
                {/* Description text */}
                {formData.assessment_type && (
                  <p className="text-xs text-gray-600 mt-2">
                    {ASSESSMENT_TYPE_DESCRIPTIONS[formData.assessment_type]}
                  </p>
                )}
                </>
              ) : (
                <p className="text-gray-800">
                  {assessment.assessment_type}
                  {assessment.assessment_type === 'JSA' && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Step-based workflow enabled</span>
                  )}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              {/* CMP-05: status is read-only here. It changes only through the
                  Submit for Review / Approve workflow (the API rejects edits). */}
              {(
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  assessment.status === 'Draft' ? 'bg-gray-100 text-gray-700' :
                  assessment.status === 'Under Review' ? 'bg-blue-100 text-blue-700' :
                  assessment.status === 'Active' ? 'bg-green-100 text-green-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {assessment.status}
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Department</label>
              {editing ? (
                <select
                  value={formData.department_id}
                  onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">— Select department —</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              ) : (
                <p className="text-gray-800">{assessment.department || 'Not specified'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-800">{assessment.location || 'Not specified'}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              {editing ? (
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-800">{assessment.description || 'No description provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Review Date</label>
              {editing ? (
                <input
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => setFormData({ ...formData, review_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  max="2099-12-31"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              ) : (
                <p className="text-gray-800">
                  {assessment.review_date
                    ? formatDate(assessment.review_date)
                    : 'Not set'}
                </p>
              )}
            </div>
          </div>

          {/* MOM Details Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-md font-semibold text-gray-800 mb-4">Minutes of Meeting (MOM) Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Meeting Date/Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">Meeting Date & Time</label>
                {editing ? (
                  <input
                    type="datetime-local"
                    value={formData.meeting_datetime}
                    onChange={(e) => setFormData({ ...formData, meeting_datetime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                ) : (
                  <p className="text-gray-800">
                    {assessment.meeting_datetime
                      ? formatDateTime(assessment.meeting_datetime)
                      : 'Not set'}
                  </p>
                )}
              </div>

              {/* Meeting Location */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">Meeting Location</label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.meeting_location}
                    onChange={(e) => setFormData({ ...formData, meeting_location: e.target.value })}
                    placeholder="e.g., Conference Room A"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                ) : (
                  <p className="text-gray-800">{assessment.meeting_location || 'Not specified'}</p>
                )}
              </div>

              {/* Facilitator */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">Facilitator</label>
                {editing ? (
                  <select
                    value={formData.facilitator_id}
                    onChange={(e) => {
                      const selectedUser = users.find(u => u.id === e.target.value);
                      setFormData({
                        ...formData,
                        facilitator_id: e.target.value,
                        facilitator_name: selectedUser ? selectedUser.name : ''
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select Facilitator</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role || 'No Role'})
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-800">{assessment.facilitator_name || 'Not specified'}</p>
                )}
              </div>

              {/* Previous Assessment */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">Previous Assessment</label>
                {editing ? (
                  <select
                    value={formData.previous_assessment_id}
                    onChange={(e) => setFormData({ ...formData, previous_assessment_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">None</option>
                    {previousAssessments.map(ra => (
                      <option key={ra.id} value={ra.id}>
                        {ra.title}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-800">
                    {previousAssessments.find(ra => ra.id === assessment.previous_assessment_id)?.title || 'None'}
                  </p>
                )}
              </div>
            </div>

            {/* Attendees */}
            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-600 mb-2">Attendees</label>
              {editing ? (
                <div>
                  <select
                    multiple
                    value={formData.mom_attendees?.map(a => a.user_id) || []}
                    onChange={(e) => {
                      const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
                      const selectedAttendees = selectedIds.map(userId => {
                        const u = users.find(user => user.id === userId);
                        return {
                          user_id: userId,
                          user_name: u?.name || '',
                          department: u?.department || '',
                          role: u?.role || ''
                        };
                      });
                      setFormData({ ...formData, mom_attendees: selectedAttendees });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg h-32"
                  >
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name} - {u.department || 'No Dept'} ({u.role || 'No Role'})
                      </option>
                    ))}
                  </select>
                  {formData.mom_attendees?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.mom_attendees.map(att => (
                        <span key={att.user_id} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm flex items-center gap-1">
                          {att.user_name}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                mom_attendees: formData.mom_attendees.filter(a => a.user_id !== att.user_id)
                              });
                            }}
                            className="ml-1 text-blue-500 hover:text-blue-700"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {assessment.mom_attendees?.length > 0 ? (
                    assessment.mom_attendees.map(att => (
                      <span key={att.user_id} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                        {att.user_name} ({att.role || att.department || 'N/A'})
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500">No attendees recorded</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* JSA Steps Section - Only for JSA type assessments */}
        {assessment.assessment_type === 'JSA' && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">📋 Job Safety Analysis - Steps</h2>
              {hasPermission('hira', 'update') && (
                <button
                  onClick={() => !isActive && openJSAModal()}
                  disabled={isActive}
                  className={`px-4 py-2 rounded-lg ${isActive ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#1B5C94] text-white hover:bg-[#164A7B]'}`}
                  title={isActive ? `Cannot edit a risk assessment in '${assessment.status}' status` : 'Add a JSA step'}
                >
                  Add JSA Step
                </button>
              )}
            </div>

            {assessment.jsa_steps && assessment.jsa_steps.length > 0 ? (
              <div className="space-y-3">
                {assessment.jsa_steps.sort((a, b) => a.step_sequence - b.step_sequence).map((step) => (
                  <div key={step.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-gray-800">Step {step.step_sequence}: {step.step_description}</h3>
                      <div className="flex gap-2">
                        {hasPermission('hira', 'update') && (
                          <button
                            onClick={() => !isActive && openJSAModal(step)}
                            disabled={isActive}
                            className={`text-sm ${isActive ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:underline'}`}
                            title={isActive ? `Cannot edit a risk assessment in '${assessment.status}' status` : 'Edit step'}
                          >
                            Edit
                          </button>
                        )}
                        {hasPermission('hira', 'delete') && (
                          <button
                            onClick={() => !isActive && handleDeleteJSAStep(step.id)}
                            disabled={isActive}
                            className={`text-sm ${isActive ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:underline'}`}
                            title={isActive ? `Cannot edit a risk assessment in '${assessment.status}' status` : 'Delete step'}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mb-2"><strong>Hazard:</strong> {step.hazard_description}</p>
                    {step.existing_controls && step.existing_controls.length > 0 && (
                      <p className="text-sm text-gray-600"><strong>Existing Controls:</strong> {step.existing_controls.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-blue-50 rounded-lg">
                <p className="text-gray-600 mb-2">No JSA steps added yet</p>
                <p className="text-sm text-gray-500">For JSA assessments, break down the job into sequential steps and identify hazards for each step.</p>
              </div>
            )}
          </div>
        )}

        {/* Hazards Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Identified Hazards & Risk Matrix</h2>
            {hasPermission('hira', 'update') && (
              <button
                onClick={() => !isActive && openHazardModal()}
                disabled={isActive}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${isActive ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#1B5C94] text-white hover:bg-[#164A7B]'}`}
                title={isActive ? `Cannot edit a risk assessment in '${assessment.status}' status` : 'Add a new hazard'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Hazard
              </button>
            )}
          </div>

          {assessment.hazards && assessment.hazards.length > 0 ? (
            <div className="space-y-4">
              {assessment.hazards.map((hazard, index) => (
                <div key={hazard.id || index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-gray-800">{hazardDisplay(hazard).name}</h3>
                        {(hazard.legal_requirement || hazard.emergency_condition) && (
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-300"
                            title={`Significant hazard (${[
                              hazard.legal_requirement && 'Legal Requirement',
                              hazard.emergency_condition && 'Emergency Condition',
                            ].filter(Boolean).join(', ')}) — significant regardless of risk score`}
                          >
                            SIGNIFICANT
                          </span>
                        )}
                      </div>
                      {hazardDisplay(hazard).sub && (
                        <p className="text-sm text-gray-600 mt-1">{hazardDisplay(hazard).sub}</p>
                      )}
                      {(hazard.process || hazard.activity || hazard.activity_type) && (
                        <p className="text-xs text-gray-500 mt-1">
                          {[
                            hazard.process && `Process: ${hazard.process}`,
                            hazard.activity && `Activity: ${hazard.activity}`,
                            hazard.activity_type && `Type: ${hazard.activity_type}`,
                          ].filter(Boolean).join(' | ')}
                        </p>
                      )}
                      {hazard.who_at_risk && (
                        <p className="text-sm text-gray-600 mt-1">At Risk: {hazard.who_at_risk}</p>
                      )}
                      {hazard.consequence && (
                        <p className="text-sm text-gray-600 mt-1">Related Risk: {hazard.consequence}</p>
                      )}
                      {(hazardCapaLinks[hazard.id] || []).length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                          <span className="text-xs font-semibold text-gray-500">
                            Linked CAPAs ({hazardCapaLinks[hazard.id].length}):
                          </span>
                          {hazardCapaLinks[hazard.id].map(link => (
                            <span
                              key={link.capa_id}
                              className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-orange-50 text-orange-700 border border-orange-200"
                              title={`${link.title} — ${link.status}`}
                            >
                              {link.title?.length > 40 ? `${link.title.slice(0, 40)}…` : link.title}
                              <span className="ml-1 text-orange-400">({link.status})</span>
                            </span>
                          ))}
                        </div>
                      )}
                      {(hazardIncidentLinks[hazard.id] || []).length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                          <span className="text-xs font-semibold text-gray-500">
                            Linked Incidents ({hazardIncidentLinks[hazard.id].length}):
                          </span>
                          {hazardIncidentLinks[hazard.id].map(link => (
                            <span
                              key={link.link_id}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200"
                              title={`${link.title} — ${link.status}`}
                            >
                              {link.incident_number ? `${formatIncidentNumber(link.incident_number)}: ` : ''}
                              {link.title?.length > 40 ? `${link.title.slice(0, 40)}…` : link.title}
                              <span className="text-rose-400">({link.status})</span>
                              {hasPermission('hira', 'update') && !isCrossSiteReadOnly && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleUnlinkIncident(link); }}
                                  className="text-rose-400 hover:text-rose-700 font-bold"
                                  title="Unlink incident"
                                >
                                  ×
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {hasPermission('hira', 'update') && !isCrossSiteReadOnly && (
                        <button
                          onClick={() => openLinkIncidentModal(hazard)}
                          className="text-rose-600 hover:underline text-sm"
                          title="Link an existing incident to this hazard (register column X)"
                        >
                          Link Incident
                        </button>
                      )}
                      {!isActive && !isCrossSiteReadOnly && hasPermission('capa', 'create') && (
                        <button
                          onClick={() => openCAPAModal(hazard, index)}
                          className="text-orange-600 hover:underline text-sm"
                        >
                          Create CAPA
                        </button>
                      )}
                      {hasPermission('hira', 'update') && (
                        <button
                          onClick={() => !isActive && openHazardModal(hazard)}
                          disabled={isActive}
                          className={`text-sm ${isActive ? 'text-gray-400 cursor-not-allowed' : 'text-[#1B5C94] hover:underline'}`}
                          title={isActive ? `Cannot edit a risk assessment in '${assessment.status}' status` : 'Edit hazard'}
                        >
                          Edit
                        </button>
                      )}
                      {hasPermission('hira', 'delete') && (
                        <button
                          onClick={() => !isActive && handleDeleteHazard(hazard.id)}
                          disabled={isActive}
                          className={`text-sm ${isActive ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:underline'}`}
                          title={isActive ? `Cannot edit a risk assessment in '${assessment.status}' status` : 'Delete hazard'}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Risk Matrix — shows Initial (I) and Residual (R) on same 5×5 grid */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-xs font-semibold text-gray-600 mb-3">Risk Matrix</p>
                    <RiskMatrix5x5
                      initialSeverity={hazard.severity}
                      initialLikelihood={hazard.likelihood}
                      residualSeverity={hazard.residual_severity || null}
                      residualLikelihood={hazard.residual_likelihood || null}
                      compact={true}
                      bands={riskBands}
                    />
                    {/* Numeric scores below matrix */}
                    <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-1.5">
                        <span className="w-4 h-4 bg-gray-800 text-white rounded-full text-[9px] flex items-center justify-center font-black shrink-0">I</span>
                        <span className="text-xs text-gray-600">
                          S:{hazard.severity} × L:{hazard.likelihood} = {hazard.risk_score}
                        </span>
                        <span className="px-1.5 py-0.5 rounded text-xs font-bold" style={getRiskBadgeStyle(hazard.risk_level)}>
                          {hazard.risk_level}
                        </span>
                      </div>
                      {hazard.residual_severity && hazard.residual_likelihood ? (
                        <div className="flex items-center gap-1.5">
                          <span className="w-4 h-4 bg-blue-600 text-white rounded-full text-[9px] flex items-center justify-center font-black shrink-0">R</span>
                          <span className="text-xs text-blue-700">
                            S:{hazard.residual_severity} × L:{hazard.residual_likelihood} = {hazard.residual_risk_score}
                          </span>
                          <span className="px-1.5 py-0.5 rounded text-xs font-bold" style={getRiskBadgeStyle(hazard.residual_risk_level)}>
                            {hazard.residual_risk_level}
                          </span>
                          {isBlockingLevel(hazard.residual_severity * hazard.residual_likelihood) && (
                            <span className="text-xs text-red-600 font-semibold">⚠️ Cannot approve</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Residual not assessed — add controls and reassess</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-gray-600">No hazards identified yet</p>
              <button
                onClick={() => !isActive && openHazardModal()}
                disabled={isActive}
                className={`mt-4 font-semibold ${isActive ? 'text-gray-400 cursor-not-allowed' : 'text-[#1B5C94] hover:underline'}`}
                title={isActive ? `Cannot edit a risk assessment in '${assessment.status}' status` : 'Add a new hazard'}
              >
                Add your first hazard
              </button>
            </div>
          )}
        </div>
          </>
        ) : (
          /* Audit History Tab */
          <div className="bg-white rounded-xl shadow-md p-6">
            <AuditTrailViewer
              entityType="risk_assessment"
              entityId={id}
              title="Risk Assessment Audit History"
            />
          </div>
        )}

        {/* Hazard Modal */}
        {showHazardModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingHazard ? 'Edit Hazard' : 'Add New Hazard'}
                </h2>
                <button
                  onClick={() => setShowHazardModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Hazard Library Selector - Searchable */}
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-green-900 mb-2">
                    📚 Select from Hazard Library or Enter Custom Hazard
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={hazardSearchText}
                      onChange={handleHazardSearchChange}
                      onFocus={() => setShowHazardDropdown(true)}
                      placeholder="Type to search hazards (e.g., 'Vib' for Vibration) or enter custom..."
                      className="w-full px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                    {hazardSearchText && (
                      <button
                        type="button"
                        onClick={() => {
                          setHazardSearchText('');
                          setSelectedLibraryHazard('');
                          setIsCustomHazard(false);
                          setShowHazardDropdown(false);
                          // Clearing the search also clears the fields that
                          // the library/custom selection auto-populated, so
                          // the user starts from a clean slate instead of
                          // editing a description still tied to the prior
                          // hazard. severity/likelihood are user-tuned and
                          // intentionally preserved.
                          setHazardForm(prev => ({
                            ...prev,
                            hazard_description: '',
                            existing_controls: [],
                            control_measures: [],
                          }));
                        }}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}

                    {/* Dropdown */}
                    {showHazardDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {/* Custom hazard option */}
                        {hazardSearchText.length >= 2 && (
                          <button
                            type="button"
                            onClick={handleUseCustomHazard}
                            className="w-full px-4 py-2 text-left hover:bg-blue-50 border-b border-gray-200 text-blue-700 font-medium"
                          >
                            ➕ Use custom hazard: "{hazardSearchText}"
                          </button>
                        )}

                        {/* Filtered hazards */}
                        {filteredHazards.length > 0 ? (
                          filteredHazards.map(h => (
                            <button
                              key={h.id}
                              type="button"
                              onClick={() => handleHazardSelect(h)}
                              className={`w-full px-4 py-2 text-left hover:bg-green-50 border-b border-gray-100 ${
                                selectedLibraryHazard === h.id ? 'bg-green-100' : ''
                              }`}
                            >
                              <span className="text-green-700 font-medium">[{h.category}]</span>{' '}
                              <span className="text-gray-800">{h.hazard_name}</span>
                            </button>
                          ))
                        ) : (
                          hazardSearchText.length >= 2 && (
                            <div className="px-4 py-2 text-gray-500 text-sm">
                              No matching hazards found. Use the custom option above.
                            </div>
                          )
                        )}

                        {hazardSearchText.length < 2 && hazardSearchText.length > 0 && (
                          <div className="px-4 py-2 text-gray-500 text-sm">
                            Type at least 2 characters to filter...
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Status indicator */}
                  {isCustomHazard && hazardSearchText && (
                    <p className="text-xs text-blue-600 mt-2">
                      ✓ Using custom hazard. Fill in the details below.
                    </p>
                  )}
                  {selectedLibraryHazard && (
                    <p className="text-xs text-green-700 mt-2">
                      ✓ Selected from library - description and controls auto-filled.
                    </p>
                  )}
                  {!isCustomHazard && !selectedLibraryHazard && (
                    <p className="text-xs text-green-700 mt-2">
                      Type to search or enter your own custom hazard name.
                    </p>
                  )}

                  {/*
                    "Save to library" opt-in, only meaningful when the user
                    is about to commit a custom hazard. The checkbox seeds
                    the shared hazard_library with the name they typed so
                    future HIRA authors can pick it from the dropdown.
                    Suggested controls on the library entry are populated
                    from whatever existing_controls / control_measures the
                    user has added on this RA hazard (see handleSaveHazard).
                  */}
                  {isCustomHazard && hazardSearchText && (
                    <div className="mt-3 p-3 bg-white border border-green-300 rounded-md">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={saveToLibrary}
                          onChange={(e) => setSaveToLibrary(e.target.checked)}
                          disabled={savingToLibrary}
                          className="w-4 h-4 text-green-600 focus:ring-green-500 rounded"
                        />
                        <span className="text-sm font-semibold text-gray-800">
                          📚 Also save this hazard to the library for future use
                        </span>
                      </label>
                      {saveToLibrary && (
                        <div className="mt-2 ml-6">
                          <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Library category
                          </label>
                          <select
                            value={libraryCategory}
                            onChange={(e) => setLibraryCategory(e.target.value)}
                            disabled={savingToLibrary}
                            className="w-full md:w-1/2 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500"
                          >
                            <option value="Physical">Physical</option>
                            <option value="Mechanical">Mechanical</option>
                            <option value="Chemical">Chemical</option>
                            <option value="Electrical">Electrical</option>
                            <option value="Biological">Biological</option>
                            <option value="Ergonomic">Ergonomic</option>
                            <option value="Psychosocial">Psychosocial</option>
                            <option value="Fire">Fire</option>
                            <option value="Environmental">Environmental</option>
                            <option value="Other">Other</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            The hazard description and any controls you add below
                            will be stored with the library entry.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Click outside to close dropdown */}
                {showHazardDropdown && (
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowHazardDropdown(false)}
                  />
                )}

                {/* Hazard Name (QA #85) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Hazard Name
                  </label>
                  <input
                    type="text"
                    value={hazardForm.title}
                    onChange={(e) => setHazardForm({ ...hazardForm, title: e.target.value })}
                    placeholder="Short name, e.g. 'Fall from Height'"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Shown as the hazard heading. Defaults to the description if left blank.
                  </p>
                </div>

                {/* Hazard Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Hazard Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={hazardForm.hazard_description}
                    onChange={(e) => setHazardForm({ ...hazardForm, hazard_description: e.target.value })}
                    rows={3}
                    placeholder="Describe the hazard in detail..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>

                {/* Who is at Risk */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Who is at Risk?
                  </label>
                  <input
                    type="text"
                    value={hazardForm.who_at_risk}
                    onChange={(e) => setHazardForm({ ...hazardForm, who_at_risk: e.target.value })}
                    placeholder="e.g., Workers, Contractors, Visitors"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>

                {/* Related Risk / Consequence */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Related Risk (Consequence)
                  </label>
                  <textarea
                    value={hazardForm.consequence}
                    onChange={(e) => setHazardForm({ ...hazardForm, consequence: e.target.value })}
                    rows={2}
                    placeholder="What could happen? e.g., burns, fall from height, hearing loss"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>

                {/* Process / Activity / Type of Activity */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Process
                    </label>
                    <input
                      type="text"
                      value={hazardForm.process}
                      onChange={(e) => setHazardForm({ ...hazardForm, process: e.target.value })}
                      placeholder="e.g., Welding shop"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Activity
                    </label>
                    <input
                      type="text"
                      value={hazardForm.activity}
                      onChange={(e) => setHazardForm({ ...hazardForm, activity: e.target.value })}
                      placeholder="e.g., Arc welding"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Type of Activity
                    </label>
                    <select
                      value={hazardForm.activity_type}
                      onChange={(e) => setHazardForm({ ...hazardForm, activity_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    >
                      <option value="Routine">Routine</option>
                      <option value="Non-Routine">Non-Routine</option>
                    </select>
                  </div>
                </div>

                {/* Overriding criteria for significant hazard */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    Overriding Criteria for Significant Hazard
                  </p>
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={hazardForm.legal_requirement}
                        onChange={(e) => setHazardForm({ ...hazardForm, legal_requirement: e.target.checked })}
                        className="rounded border-gray-300 text-[#1B5C94] focus:ring-[#1B5C94]"
                      />
                      Legal Requirement
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={hazardForm.emergency_condition}
                        onChange={(e) => setHazardForm({ ...hazardForm, emergency_condition: e.target.checked })}
                        className="rounded border-gray-300 text-[#1B5C94] focus:ring-[#1B5C94]"
                      />
                      Emergency Condition
                    </label>
                  </div>
                  {(hazardForm.legal_requirement || hazardForm.emergency_condition) && (
                    <div className="mt-3 bg-red-50 border-l-4 border-red-500 p-3">
                      <p className="text-sm text-red-700 font-semibold">
                        Significant hazard — overrides risk score. At least one control
                        measure is required before this assessment can be approved.
                      </p>
                    </div>
                  )}
                </div>

                {/* Risk Matrix - 5x5 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="font-bold text-gray-800 mb-4">Risk Assessment (5×5 Matrix)</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Severity (Consequence) <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={hazardForm.severity}
                        onChange={(e) => setHazardForm({ ...hazardForm, severity: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      >
                        {scaleLabels.severity.map(s => (
                          <option key={s.value} value={s.value} title={s.description || ''}>
                            {s.value} - {s.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Likelihood (Probability) <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={hazardForm.likelihood}
                        onChange={(e) => setHazardForm({ ...hazardForm, likelihood: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      >
                        {scaleLabels.probability.map(p => (
                          <option key={p.value} value={p.value} title={p.description || ''}>
                            {p.value} - {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Risk Score Display */}
                  <div className="bg-white rounded-lg p-4 border-2 border-gray-300">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Risk Score</p>
                        <p className="text-3xl font-bold text-gray-800">
                          {calculateRiskScore(hazardForm.severity, hazardForm.likelihood)}
                        </p>
                      </div>
                      <div>
                        <span
                          className="px-4 py-2 rounded-lg text-lg font-bold"
                          style={getRiskBadgeStyle(calculateRiskLevel(calculateRiskScore(hazardForm.severity, hazardForm.likelihood)))}
                        >
                          {calculateRiskLevel(calculateRiskScore(hazardForm.severity, hazardForm.likelihood))}
                        </span>
                      </div>
                    </div>
                    {/* Live matrix preview — shows I marker, updates as dropdowns change */}
                    <RiskMatrix5x5
                      initialSeverity={hazardForm.severity}
                      initialLikelihood={hazardForm.likelihood}
                      residualSeverity={hazardForm.residual_severity || null}
                      residualLikelihood={hazardForm.residual_likelihood || null}
                      showLegend={true}
                      bands={riskBands}
                    />
                  </div>
                </div>

                {/* Existing Controls */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Existing Controls
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={existingControlInput}
                      onChange={(e) => setExistingControlInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExistingControl())}
                      placeholder="Enter existing control measure"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    />
                    <select
                      value={existingControlType}
                      onChange={(e) => setExistingControlType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] text-sm"
                      title="Hierarchy of controls"
                    >
                      <option value="Elimination">Elimination</option>
                      <option value="Substitution">Substitution</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Administrative">Administrative</option>
                      <option value="PPE">PPE</option>
                    </select>
                    <button
                      type="button"
                      onClick={addExistingControl}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Add
                    </button>
                  </div>
                  {hazardForm.existing_controls.length > 0 && (
                    <div className="space-y-2">
                      {normalizeExistingControls(hazardForm.existing_controls).map((ctrl, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-700">
                            {ctrl.description}
                            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-200 text-gray-600">
                              {ctrl.control_type}
                            </span>
                          </span>
                          <button
                            onClick={() => removeExistingControl(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Residual Risk (After Controls) */}
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6">
                  <h3 className="font-bold text-blue-900 mb-4">Residual Risk Assessment (After Controls)</h3>
                  <p className="text-sm text-blue-700 mb-4">After implementing control measures, reassess the risk:</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-blue-900 mb-2">
                        Residual Severity
                      </label>
                      <select
                        value={hazardForm.residual_severity || ''}
                        onChange={(e) => setHazardForm({ ...hazardForm, residual_severity: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Not assessed yet --</option>
                        {scaleLabels.severity.map(s => (
                          <option key={s.value} value={s.value} title={s.description || ''}>
                            {s.value} - {s.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-blue-900 mb-2">
                        Residual Likelihood
                      </label>
                      <select
                        value={hazardForm.residual_likelihood || ''}
                        onChange={(e) => setHazardForm({ ...hazardForm, residual_likelihood: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Not assessed yet --</option>
                        {scaleLabels.probability.map(p => (
                          <option key={p.value} value={p.value} title={p.description || ''}>
                            {p.value} - {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Residual Risk Score Display */}
                  {hazardForm.residual_severity && hazardForm.residual_likelihood && (
                    <div className="bg-white rounded-lg p-4 border-2 border-blue-400">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm text-blue-700">Residual Risk Score</p>
                          <p className="text-3xl font-bold text-blue-900">
                            {calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood)}
                          </p>
                        </div>
                        <div>
                          <span
                            className="px-4 py-2 rounded-lg text-lg font-bold"
                            style={getRiskBadgeStyle(calculateRiskLevel(calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood)))}
                          >
                            {calculateRiskLevel(calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood))}
                          </span>
                        </div>
                      </div>
                      {/* Combined matrix: shows both I (initial) and R (residual) markers */}
                      <RiskMatrix5x5
                        initialSeverity={hazardForm.severity}
                        initialLikelihood={hazardForm.likelihood}
                        residualSeverity={hazardForm.residual_severity}
                        residualLikelihood={hazardForm.residual_likelihood}
                        showLegend={true}
                        bands={riskBands}
                      />
                      {isBlockingLevel(calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood)) && (
                        <div className="mt-3 bg-red-50 border-l-4 border-red-500 p-3">
                          <p className="text-sm text-red-700 font-semibold">
                            ⚠️ Warning: residual risk of '{calculateRiskLevel(calculateRiskScore(hazardForm.residual_severity, hazardForm.residual_likelihood))}' is an unacceptable band and will prevent approval
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* AI Suggestions */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <h4 className="font-bold text-purple-900">AI-Powered Control Suggestions</h4>
                    </div>
                    <button
                      type="button"
                      onClick={fetchAISuggestions}
                      disabled={loadingAI || !hazardForm.hazard_description.trim()}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-semibold"
                    >
                      {loadingAI ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Get AI Suggestions
                        </>
                      )}
                    </button>
                  </div>

                  {aiSuggestions.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs text-purple-700 mb-2">Click on a suggestion to add it as a control measure:</p>
                      {aiSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => addAISuggestion(suggestion)}
                          className="bg-white p-3 rounded-lg border border-purple-200 hover:border-purple-400 hover:shadow-md cursor-pointer transition-all"
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-lg">
                              {suggestion.control_type === 'Elimination' ? '🚫' :
                               suggestion.control_type === 'Substitution' ? '🔄' :
                               suggestion.control_type === 'Engineering' ? '🔧' :
                               suggestion.control_type === 'Administrative' ? '📋' : '🦺'}
                            </span>
                            <div className="flex-1">
                              <div className="font-semibold text-sm text-purple-900">{suggestion.control_type}</div>
                              <div className="text-sm text-gray-700">{suggestion.description}</div>
                            </div>
                            <button className="text-purple-600 hover:text-purple-800 text-xs font-semibold">
                              Add →
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-purple-700 text-center py-2">
                      {loadingAI ? 'AI is analyzing the hazard...' : 'Enter hazard details and click "Get AI Suggestions" for intelligent control recommendations'}
                    </p>
                  )}
                </div>

                {/* Control Measures (Hierarchy of Controls) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    New Control Measures (Hierarchy of Controls)
                  </label>
                  <div className="space-y-3 mb-3">
                    <select
                      value={newControl.control_type}
                      onChange={(e) => setNewControl({ ...newControl, control_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    >
                      <option value="Elimination">🚫 Elimination (Most Effective)</option>
                      <option value="Substitution">🔄 Substitution</option>
                      <option value="Engineering">🔧 Engineering Controls</option>
                      <option value="Administrative">📋 Administrative Controls</option>
                      <option value="PPE">🦺 PPE (Least Effective)</option>
                    </select>
                    <input
                      type="text"
                      value={newControl.description}
                      onChange={(e) => setNewControl({ ...newControl, description: e.target.value })}
                      placeholder="Control measure description"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={newControl.responsibility}
                        onChange={(e) => setNewControl({ ...newControl, responsibility: e.target.value })}
                        placeholder="Responsible person"
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                      <input
                        type="text"
                        value={newControl.timeline}
                        onChange={(e) => setNewControl({ ...newControl, timeline: e.target.value })}
                        placeholder="Timeline (e.g., 30 days)"
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={addControlMeasure}
                      className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Add Control Measure
                    </button>
                  </div>

                  {hazardForm.control_measures.length > 0 && (
                    <div className="space-y-2">
                      {hazardForm.control_measures.map((ctrl, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded border border-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <select
                              value={ctrl.control_type}
                              onChange={(e) => updateControlMeasure(index, 'control_type', e.target.value)}
                              className="text-sm font-semibold text-[#1B5C94] bg-white border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-[#1B5C94]"
                            >
                              <option value="Elimination">Elimination</option>
                              <option value="Substitution">Substitution</option>
                              <option value="Engineering">Engineering</option>
                              <option value="Administrative">Administrative</option>
                              <option value="PPE">PPE</option>
                            </select>
                            <button
                              onClick={() => removeControlMeasure(index)}
                              className="text-red-600 hover:text-red-800 ml-2"
                            >
                              ×
                            </button>
                          </div>
                          <input
                            type="text"
                            value={ctrl.description}
                            onChange={(e) => updateControlMeasure(index, 'description', e.target.value)}
                            className="w-full text-sm text-gray-700 bg-white border border-gray-300 rounded px-2 py-1 mb-2 focus:ring-2 focus:ring-[#1B5C94]"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={ctrl.responsibility || ''}
                              onChange={(e) => updateControlMeasure(index, 'responsibility', e.target.value)}
                              placeholder="Responsible person"
                              className="text-xs text-gray-600 bg-white border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-[#1B5C94]"
                            />
                            <input
                              type="text"
                              value={ctrl.timeline || ''}
                              onChange={(e) => updateControlMeasure(index, 'timeline', e.target.value)}
                              placeholder="Timeline"
                              className="text-xs text-gray-600 bg-white border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-[#1B5C94]"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowHazardModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveHazard}
                  disabled={!hazardForm.hazard_description}
                  className="px-6 py-2 bg-[#1B5C94] text-white rounded-lg hover:bg-[#164A7B] disabled:bg-gray-400"
                >
                  {editingHazard ? 'Update Hazard' : 'Save Hazard'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Version Modal */}
        {showVersionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Create New Version</h2>
              <p className="text-sm text-gray-600 mb-4">This will archive the current approved version and create a new draft.</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Change Type</label>
                  <select
                    id="changeType"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    defaultValue="Minor"
                  >
                    <option value="Minor">Minor ({assessment.version_label || `v${assessment.version_number || 1}.0`} → v{(assessment.version_number || 1) + 1}.0)</option>
                    <option value="Major">Major ({assessment.version_label || `v${assessment.version_number || 1}.0`} → v{(assessment.version_number || 1) + 1}.0)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Change Notes</label>
                  <textarea
                    id="changeNotes"
                    rows={3}
                    placeholder="Describe what changes will be made..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowVersionModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const changeType = document.getElementById('changeType').value;
                    const notes = document.getElementById('changeNotes').value;
                    handleCreateVersion(changeType, notes);
                    setShowVersionModal(false);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Version
                </button>
              </div>
            </div>
          </div>
        )}

        {/* JSA Step Modal */}
        {showJSAModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingJSAStep ? 'Edit JSA Step' : 'Add JSA Step'}
                </h2>
                <button
                  onClick={() => setShowJSAModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Step Sequence <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={jsaStepForm.step_sequence}
                    onChange={(e) => setJsaStepForm({ ...jsaStepForm, step_sequence: parseInt(e.target.value) })}
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Step Description <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={jsaStepForm.step_description}
                    onChange={(e) => setJsaStepForm({ ...jsaStepForm, step_description: e.target.value })}
                    placeholder="e.g., Climb ladder to access roof"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Hazard Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={jsaStepForm.hazard_description}
                    onChange={(e) => setJsaStepForm({ ...jsaStepForm, hazard_description: e.target.value })}
                    rows={3}
                    placeholder="Describe the hazards associated with this step..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Existing Controls
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={jsaControlInput}
                      onChange={(e) => setJsaControlInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addJSAControl())}
                      placeholder="Enter control measure"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={addJSAControl}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  {jsaStepForm.existing_controls.length > 0 && (
                    <div className="space-y-2">
                      {jsaStepForm.existing_controls.map((ctrl, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-700">{ctrl}</span>
                          <button
                            onClick={() => removeJSAControl(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <p className="text-sm text-blue-700">
                    <strong>JSA Tip:</strong> Break down the job into logical steps. For each step, identify all potential hazards 
                    and the controls needed to mitigate them.
                  </p>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowJSAModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveJSAStep}
                  disabled={!jsaStepForm.step_description || !jsaStepForm.hazard_description}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {editingJSAStep ? 'Update Step' : 'Save Step'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CAPA Modal */}
        {/* Link Incident picker (register column X — hazard-level traceability) */}
        {linkIncidentHazard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Link Incident to Hazard</h2>
                  <p className="text-sm text-gray-500 truncate max-w-md">
                    {linkIncidentHazard.hazard_description}
                  </p>
                </div>
                <button onClick={() => setLinkIncidentHazard(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">
                  ×
                </button>
              </div>

              <div className="px-6 py-4">
                <input
                  type="text"
                  value={incidentSearch}
                  onChange={(e) => setIncidentSearch(e.target.value)}
                  placeholder="Search incidents by title or number…"
                  autoFocus
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94] focus:outline-none"
                />
              </div>

              <div className="px-6 pb-4 overflow-y-auto flex-1 space-y-2">
                {incidentSearchLoading ? (
                  <p className="text-sm text-gray-500 py-4 text-center">Searching…</p>
                ) : incidentSearchError ? (
                  <p className="text-sm text-red-600 py-4 text-center">{incidentSearchError}</p>
                ) : incidentResults.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">No incidents found.</p>
                ) : (
                  incidentResults.map(inc => {
                    const alreadyLinked = (hazardIncidentLinks[linkIncidentHazard.id] || [])
                      .some(l => l.incident_id === inc.id);
                    return (
                      <div key={inc.id} className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">
                            {inc.incident_number ? (
                              <span className="font-mono text-xs bg-gray-100 rounded px-1.5 py-0.5 mr-2">{formatIncidentNumber(inc.incident_number)}</span>
                            ) : null}
                            {inc.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {inc.severity ? `Severity: ${inc.severity}` : ''}{inc.severity && inc.status ? ' · ' : ''}{inc.status || ''}
                          </p>
                        </div>
                        {alreadyLinked ? (
                          <span className="text-xs font-semibold text-gray-400 whitespace-nowrap">Linked</span>
                        ) : (
                          <button
                            onClick={() => handleLinkIncident(inc.id)}
                            className="px-3 py-1 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg whitespace-nowrap"
                          >
                            Link
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="border-t border-gray-200 px-6 py-3 flex justify-end">
                <button
                  onClick={() => setLinkIncidentHazard(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {showCAPAModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Create CAPA from HIRA</h2>
              <p className="text-sm text-gray-600 mb-4">Link a corrective/preventive action to a hazard in this risk assessment</p>

              <div className="space-y-4">
                {/* Hazard Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Hazard <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={capaForm.hazard_id}
                    onChange={(e) => setCAPAForm({ ...capaForm, hazard_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">-- Select a hazard --</option>
                    {assessment.hazards?.map((hazard, index) => (
                      <option key={hazard.id || index} value={hazard.id || String(index)}>
                        {hazard.hazard_description} ({hazard.risk_level})
                      </option>
                    ))}
                  </select>
                </div>

                {/* QA #87b: QA reported the HIRA CAPA form has no Department while
                    the standalone form does, and asked that it INHERIT the HIRA's
                    department. It already does server-side
                    (routes/hira.py create_capa_from_hazard passes department and
                    department_id from the risk assessment) - the inheritance was
                    just invisible here. Read-only by design: overriding it would
                    let a CAPA disagree with the assessment that produced it. */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Department
                  </label>
                  <div className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                    {departmentLabel(assessment)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Inherited from this risk assessment.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Corrective Action</label>
                  <textarea
                    value={capaForm.corrective_action}
                    onChange={(e) => setCAPAForm({ ...capaForm, corrective_action: e.target.value })}
                    rows={3}
                    placeholder="Describe the corrective action to address the hazard..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Preventive Action</label>
                  <textarea
                    value={capaForm.preventive_action}
                    onChange={(e) => setCAPAForm({ ...capaForm, preventive_action: e.target.value })}
                    rows={3}
                    placeholder="Describe the preventive action to avoid recurrence..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Assigned To <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={capaForm.responsible_person_id}
                    onChange={(e) => {
                      const picked = assignableUsers.find(u => u.id === e.target.value);
                      setCAPAForm({
                        ...capaForm,
                        responsible_person_id: e.target.value,
                        responsible_person: picked ? picked.name : '',
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">— Select a person —</option>
                    {assignableUsers.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name}{u.role ? ` (${formatRoleName(u.role)})` : ''}
                      </option>
                    ))}
                  </select>
                  {assignableUsers.length === 0 && (
                    <p className="text-xs text-amber-700 mt-1">
                      No assignable users found for this site.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={capaForm.due_date}
                    onChange={(e) => setCAPAForm({ ...capaForm, due_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    max="2099-12-31"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCAPAModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCAPA}
                  disabled={!capaForm.hazard_id || !capaForm.due_date || !capaForm.responsible_person_id}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Create CAPA
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default HIRADetail;
